<template>
  <nav class="snav" aria-label="Site sections">
    <div class="snav-inner">
      <button class="snav-burger" @click="menuOpen = !menuOpen" :aria-expanded="String(menuOpen)" aria-label="Menu">
        <span class="snav-burger-ico">{{ menuOpen ? '✕' : '☰' }}</span>
      </button>

      <div class="snav-tabs" :class="{ open: menuOpen }">
        <div v-for="t in tabs" :key="t.key" class="snav-item" :class="[{ 'has-children': t.children }, 'snav-item-' + t.key]">
          <a :href="t.href" class="snav-tab" :class="['snav-' + t.key, { active: t.key === activeKey }]"
             :aria-current="t.key === activeKey ? 'page' : null" @click="menuOpen = false">
            <span class="snav-emoji" aria-hidden="true">{{ t.emoji }}</span><span class="snav-label">{{ t.label }}</span>
            <span v-if="t.children" class="snav-caret" aria-hidden="true">▾</span>
          </a>

          <!-- Players / Champions: a mega-menu of related pages & in-page modes -->
          <div v-if="t.children" class="snav-mega">
            <div class="snav-mega-head"><span class="snav-mega-title">{{ t.emoji }} {{ t.label }}</span><span class="snav-mega-sub">{{ t.desc }}</span></div>
            <div class="snav-mega-grid">
              <a v-for="c in t.children" :key="c.href" :href="c.href" class="snav-sub" :class="{ active: childActive(c.href) }" @click="menuOpen = false">
                <span class="snav-sub-label">{{ c.label }}</span>
                <span class="snav-sub-desc">{{ c.desc }}</span>
              </a>
            </div>
          </div>

          <!-- other tabs: a verbose styled hover tooltip -->
          <span v-else-if="t.desc" class="snav-tip">{{ t.desc }}</span>
        </div>
      </div>

      <div class="snav-actions"><slot name="actions" /></div>
    </div>
  </nav>
</template>

<script>
import { SECTIONS, resolveSection } from '../routes.js';
// Shared top navigation across every page. Each view renders <SiteNav active="…"/>
// at the top; the homepage also fills the #actions slot with its bracket controls
// (settings / help / generator / prev-tournament). Self-detects the active tab from
// the URL params when `active` isn't passed. The Players tab opens a mega-menu of
// its in-page modes (deep-linked via ?players&tab=…); the other tabs get a verbose
// styled hover tooltip. On phones the whole tab row collapses behind a ☰ hamburger.
// Champions is also a mega-menu: the ToC leaderboard plus the championship-history
// pages (Line of Succession, Streaks, Champions-by-class — the last two physically
// live in PlayerView under ?players&tab=…, cross-listed here as shortcuts).
// Chat is a mega-menu too: the volume hero chart (?chat) plus the other chat-mined
// pages — Social graph, Lexicon, What Chat Forces, and the FAQ. The 🎮 Game tab is a
// MENU-ONLY parent (no page) grouping the reference pages — Maps, Stats, Mechanics,
// Sprites, Gear Lab (all nav:false, activeAs:'game') — to keep the top bar short.
const CHAMPION_TABS = [
  { label: '🏆 Tournament of Champions', desc: 'The reigning-champion leaderboard by title-defense streak, with a seeded bracket and sim odds', href: '?champions' },
  { label: '👑 Line of Succession', desc: 'The complete crown lineage — every reign in order, who dethroned whom', href: '?crown' },
  { label: '🏅 Best & Worst Teams', desc: 'The highest- and lowest-graded bracket teams ever assembled — all-time & modern', href: '?teams' },
  { label: '📅 Seasons', desc: 'Champions season by season — per-season title boards, longest reigns, rookies & records', href: '?seasons' },
  { label: '🎲 Season Units', desc: 'What actually got rolled each season — every class from rarest to most common, and who rolled it most', href: '?units' },
  { label: '🎖️ VIP Quest', desc: 'The season race to champion the most different classes — the standings and everyone’s checklist of 75', href: '?vip' },
  { label: '🏈 Preshow Draft', desc: 'The ToC preshow draft room — on the clock, the live board and every team’s picks', href: '?draft' },
  { label: '📮 Draft Pool', desc: 'Every unit put forward for the draft — browse, filter, and submit your own', href: '?submissions' },
  { label: '🔥 Streaks', desc: 'Longest championship reigns & defense streaks', href: '?players&tab=streaks' },
  { label: '🏆 Champions by class', desc: 'Per-class title leaderboards + how-to-champ guide', href: '?players&tab=champions' },
];

const CHAT_TABS = [
  { label: '💬 Chat Activity', desc: 'Daily chat volume over six years + the most-active-chatter boards', href: '?chat' },
  { label: '📰 The Daily Chocobo', desc: 'Yesterday in chat, as a tabloid: the big moments, the quotes, the numbers', href: '?chocobo' },
  { label: '🕸️ Social Graph', desc: 'Who @mentions whom — friend clusters & the biggest feuds', href: '?social' },
  { label: '📖 Lexicon', desc: 'Emote & copypasta lifecycles — born, peaked, faded', href: '?lexicon' },
  { label: '🧾 Copypasta', desc: 'The long-form copypasta — every repost-pasta, ranked', href: '?copypasta' },
  { label: '📖 Holy Carpet Bible', desc: "Technominari's long verses, sorted Canon / Gospel / Apocrypha — is it all copypasta?", href: '?carpet' },
  { label: '🎡 Technospeak Wheel', desc: 'Spin the church-word wheel to build nonsense scripture (the sekhretpoop toy)', href: '?sekhretpoop' },
  { label: '🎲 What Chat Forces', desc: "Most-forced classes & skills vs win rate", href: '?forced' },
  { label: '🎁 Gifts', desc: 'Who has gifted the most skills — confirmed by the bot, not by who typed !gift', href: '?gifts' },
  { label: '❓ FAQ', desc: 'The questions chat actually asks, answered', href: '?faq' },
];

const PLAYER_TABS = [
  { label: 'Directory', desc: 'Search 16k players — win rate, mains, championships', href: '?players' },
  { label: '🏆 Champions by class', desc: 'Per-class title leaderboards + how-to-champ guide', href: '?players&tab=champions' },
  { label: '🏅 Quests', desc: 'Monster-Quest completions, checklists & badges', href: '?players&tab=quests' },
  { label: '📊 Boards', desc: 'Career, dynasty, speedrun & monster-family boards', href: '?players&tab=boards' },
  { label: '🏜️ Droughts', desc: 'Longest streaks without champing a class', href: '?players&tab=droughts' },
  { label: '💰 Betting', desc: 'Gambler profiles — ROI, win rate, balance over time', href: '?players&tab=betting' },
  { label: '🔥 Streaks', desc: 'Longest championship reigns & defense streaks', href: '?players&tab=streaks' },
  { label: '🧭 Strategy', desc: 'Best classes to champ + the empirical roll guide', href: '?players&tab=strategy' },
  { label: '🏛️ Hall of Fame', desc: 'The 350+ Club — superlatives, records & the elite roster', href: '?hall' },
  { label: '⚔️ Compare', desc: 'Side-by-side career comparison of any two players', href: '?compare' },
];

// The 🎮 Game tab groups the game-reference pages. Unlike the other mega-menus it's
// MENU-ONLY — there's no "Game" page, so the parent links to its first child (Maps);
// the grouped sections are nav:false (activeAs:'game') in routes.js and get injected
// here. (Keeps the top bar to ~7 tabs.)
const GAME_TABS = [
  { label: '🗺️ Maps', desc: 'All 138 battle maps — class win-rate edges, 3D terrain & a height calculator', href: '?maps' },
  { label: '📊 Stats', desc: 'Win-rate analytics by class, skill, gear & Brave/Faith, with Meta-Tides trends', href: '?stats' },
  { label: '📖 Mechanics', desc: 'How the battle engine works — damage, evasion, zodiac, Faith and turn order', href: '?mechanics' },
  { label: '🎞️ Sprites', desc: 'Every battle sprite off the disc — recolored walk cycles, with GIF export', href: '?gallery' },
  { label: '🔍 Gear Lab', desc: 'Item-combo lookup — roll rate, win rate, top classes, and what it pairs with', href: '?gear' },
  { label: '🎒 Armory', desc: 'Item glossary — every piece of gear with full stats, swing-damage calc & usage', href: '?armory' },
  { label: '✨ Abilities', desc: 'Skill glossary — every action skill, support, reaction & move, with output & win rates', href: '?abilities' },
  { label: '🎭 Classes', desc: 'Every job & monster — default stats by gender with walking sprites, kit, win rates & a live ability/damage card', href: '?classes' },
];

// The mega-menus stay here (SiteNav presentation detail); the top-level tab list
// + active state are derived from the shared section registry (src/routes.js), so
// adding a page is one entry there — no edit in this file.
const CHILDREN = { champions: CHAMPION_TABS, players: PLAYER_TABS, chat: CHAT_TABS, game: GAME_TABS };
const GAME_TAB = { key: 'game', emoji: '🎮', label: 'Game', href: '?maps',
  desc: 'Game data & reference — maps, win-rate stats, battle mechanics, sprites and the gear lookup.', children: GAME_TABS };
const TABS = [];
for (const s of SECTIONS.filter((x) => x.nav)) {
  if (s.key === 'build') TABS.push(GAME_TAB);   // 🎮 Game sits just before Builder
  TABS.push({ key: s.key, emoji: s.emoji, label: s.label, href: s.href || ('?' + s.param), desc: s.desc, children: CHILDREN[s.key] });
}

export default {
  name: 'SiteNav',
  props: { active: { type: String, default: null } },
  data() { return { tabs: TABS, menuOpen: false }; },
  computed: {
    activeKey() {
      if (this.active) return this.active;          // App / Luxstadio pass it explicitly
      const s = resolveSection();
      return s.activeAs === undefined ? s.key : s.activeAs;   // null = no tab claims active (e.g. changelog)
    },
  },
  methods: {
    // Highlight the active child in any mega-menu (Players + Champions). Standalone
    // pages match by a bare param (?hall/?compare/?crown/?champions); ?players children
    // match on ?tab= (directory = default). Menu-agnostic: only one child matches a URL.
    childActive(href) {
      const cur = new URLSearchParams(window.location.search);
      const h = new URLSearchParams((href.split('?')[1] || ''));
      for (const k of ['hall', 'compare', 'crown', 'seasons', 'units', 'vip', 'draft', 'submissions', 'teams', 'champions', 'chat', 'social', 'lexicon', 'copypasta', 'carpet', 'sekhretpoop', 'forced', 'gifts', 'faq', 'maps', 'stats', 'mechanics', 'gallery', 'gear']) if (h.has(k)) return cur.has(k);
      if (h.has('players')) return cur.has('players') && (cur.get('tab') || 'directory') === (h.get('tab') || 'directory');
      return false;
    },
  },
};
</script>

<style scoped>
.snav { position: sticky; top: 0; z-index: 400; background: linear-gradient(180deg, #0c2238, #0a1c2e); border-bottom: 1px solid #2f5d86; box-shadow: 0 2px 10px rgba(0, 0, 0, 0.35); }
.snav-inner { position: relative; display: flex; align-items: stretch; justify-content: space-between; gap: 10px; max-width: 1440px; margin: 0 auto; padding: 0 10px; }
.snav-tabs { display: flex; align-items: stretch; gap: 2px; overflow: visible; }
.snav-item { position: relative; display: flex; align-items: stretch; }
.snav-tab { display: flex; align-items: center; gap: 6px; padding: 8px 11px; color: #b9d6ee; text-decoration: none; font-size: 14px; white-space: nowrap; border-bottom: 3px solid transparent; transition: background 0.12s ease, color 0.12s ease; font-family: inherit; }
.snav-tab:hover { background: rgba(28, 110, 164, 0.4); color: #fff; }
.snav-tab.active { color: #fff; border-bottom-color: #ffd877; background: rgba(28, 110, 164, 0.22); }
.snav-emoji { font-size: 16px; line-height: 1; }
.snav-label { font-weight: 600; }
.snav-caret { font-size: 9px; opacity: 0.6; margin-left: 1px; }
.snav-actions { display: flex; align-items: center; gap: 6px; flex: 0 0 auto; }

/* Players mega-menu (desktop: hover / focus) */
.snav-mega { display: none; position: absolute; top: 100%; left: 0; width: 540px; max-width: 92vw; background: #0c2238; border: 1px solid #2f5d86; border-top: none; border-radius: 0 0 12px 12px; box-shadow: 0 12px 28px rgba(0, 0, 0, 0.5); z-index: 401; padding: 12px 14px 14px; }
.snav-item.has-children:hover .snav-mega,
.snav-item.has-children:focus-within .snav-mega { display: block; }
/* Chat is the rightmost tab — open its menu leftward so it doesn't run off-screen. */
.snav-item-chat .snav-mega { left: auto; right: 0; }
.snav-mega-head { display: flex; align-items: baseline; gap: 10px; padding: 2px 4px 10px; border-bottom: 1px solid rgba(47, 93, 134, 0.5); margin-bottom: 8px; flex-wrap: wrap; }
.snav-mega-title { font-family: "BossBattle"; font-size: 17px; color: #fff; }
.snav-mega-sub { font-size: 12px; color: #9fd0f0; opacity: 0.85; }
.snav-mega-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px; }
.snav-sub { display: block; padding: 8px 10px; border-radius: 7px; text-decoration: none; border-left: 3px solid transparent; }
.snav-sub:hover { background: rgba(28, 110, 164, 0.5); }
.snav-sub.active { border-left-color: #ffd877; background: rgba(28, 110, 164, 0.28); }
.snav-sub-label { display: block; font-size: 13.5px; font-weight: 600; color: #eaf4ff; }
.snav-sub-desc { display: block; font-size: 11px; color: #9fbcd6; line-height: 1.35; margin-top: 1px; }

/* Verbose hover tooltip for the non-Players tabs (matches the site .toolTip look, dropped below the bar) */
.snav-tip { display: none; position: absolute; top: calc(100% + 6px); left: 50%; transform: translateX(-50%); width: max-content; max-width: 260px; background: #0c1c2b; color: #fff; border: 1px solid #4a6f90; border-radius: 6px; padding: 8px 10px; font-size: 12px; line-height: 1.4; text-align: left; white-space: normal; box-shadow: 0 6px 16px rgba(0, 0, 0, 0.5); z-index: 401; pointer-events: none; }
.snav-item:hover > .snav-tip { display: block; }

/* Hamburger (mobile only) */
.snav-burger { display: none; width: 42px; height: 38px; background: none; border: none; color: #cbe6ff; font-size: 20px; cursor: pointer; padding: 0; }
.snav-burger:hover { color: #fff; }

@media (max-width: 860px) {
  .snav-burger { display: flex; align-items: center; justify-content: center; }
  .snav-tabs { display: none; position: absolute; top: 100%; left: 0; right: 0; flex-direction: column; gap: 0; background: #0a1c2e; border-bottom: 1px solid #2f5d86; box-shadow: 0 10px 22px rgba(0, 0, 0, 0.5); max-height: 82vh; overflow-y: auto; }
  .snav-tabs.open { display: flex; }
  .snav-item { display: block; }
  .snav-tab { border-bottom: 1px solid rgba(47, 93, 134, 0.3); border-left: 3px solid transparent; }
  .snav-tab.active { border-bottom-color: rgba(47, 93, 134, 0.3); border-left-color: #ffd877; }
  .snav-caret { display: none; }
  .snav-tip { display: none !important; }   /* no hover on touch */
  /* mega-menu becomes an inline single-column list inside the drawer */
  .snav-mega { display: block; position: static; width: auto; max-width: none; border: none; border-radius: 0; box-shadow: none; background: rgba(0, 0, 0, 0.22); padding: 0; }
  .snav-mega-head { display: none; }
  .snav-mega-grid { grid-template-columns: 1fr; gap: 0; }
  .snav-sub { border-radius: 0; padding: 8px 10px 8px 40px; border-bottom: 1px solid rgba(47, 93, 134, 0.2); }
  .snav-sub-desc { display: none; }
}
</style>
